import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Spin,
  Space,
  message,
  Table,
  Tag,
  Empty,
  Pagination,
  Modal
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  ShoppingOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import {
  MobileRecordList,
  MobileRecordCard,
  MobileCardPagination
} from '../components/common/MobileRecordCard'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api from '../utils/api'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

const { RangePicker } = DatePicker
const { Option } = Select

// Premium color palette
const colors = {
  primary: '#2C3E50',
  secondary: '#5D7A9B',
  accent: '#8B9DC3',
  text: {
    primary: '#1A1A1A',
    secondary: '#6B7280',
    tertiary: '#9CA3AF'
  },
  background: {
    primary: '#FFFFFF',
    secondary: '#FAFBFC',
    tertiary: '#F5F7FA'
  },
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444'
}

const spacing = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px'
}

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${spacing.lg} ${spacing.md};
  background: ${colors.background.secondary};
  min-height: 100vh;

  @media (max-width: 820px) {
    padding: ${spacing.md} ${spacing.sm};
  }
`

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 300;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing.xl} 0;
  letter-spacing: -0.5px;

  @media (max-width: 820px) {
    font-size: 24px;
    margin-bottom: ${spacing.lg};
  }
`

const FilterCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: ${spacing.lg};

  .ant-card-body {
    padding: ${spacing.lg};

    @media (max-width: 820px) {
      padding: ${spacing.md};
    }
  }
`

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: ${spacing.sm};
  }
`

const FilterActions = styled.div`
  display: flex;
  gap: ${spacing.sm};
  margin-top: ${spacing.md};

  @media (max-width: 820px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const TableCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .ant-card-body {
    padding: ${spacing.lg};

    @media (max-width: 820px) {
      padding: ${spacing.md};
    }
  }
`

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing.lg};

  @media (max-width: 820px) {
    justify-content: center;
  }
`

const statusLabels = {
  paid: { label: '已付款', color: 'success' },
  paid_partially: { label: '部分付款', color: 'warning' },
  confirmed: { label: '已確認', color: 'blue' },
  voided: { label: '已作廢', color: 'error' }
}

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0'
  const numeric = Number(value) || 0
  return `$${numeric.toLocaleString()}`
}

const orderTypeLabels = {
  purchase: { label: '自購', color: 'blue' },
  material: { label: '領料', color: 'green' },
  gift: { label: '贈出', color: 'orange' }
}

const paymentMethodLabels = {
  cash: '現金',
  nonCash: '非現金',
  salaryDeduct: '由薪資扣除'
}

const EmployeeOrderRecordsPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const navigate = useNavigate()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [employees, setEmployees] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  const filtersRef = useRef({})
  const pageSizeRef = useRef(10)
  const initializedRef = useRef(false)
  const loadingRef = useRef(false)

  const fetchEmployees = useCallback(async () => {
    if (!user?.businessId || !hasFeature('checkout')) return
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }, [user?.businessId, hasFeature]) // 移除 isBrandPlan 依賴，在函數內部檢查

  const fetchRecords = useCallback(
    async (page = 1, pageSize = 10, filters = {}) => {
      if (!user?.businessId || !hasFeature('checkout')) return
      if (loadingRef.current) return // 防止重複調用
      loadingRef.current = true
      setLoading(true)

      try {
        const params = {
          page,
          pageSize,
          type: 'employee_order', // 指定查詢員工訂單
          // 預設只取有效訂單，避免從庫存使用紀錄取消後仍出現在列表造成誤會。
          // 可在篩選器切換為 'voided' 或 'all'。
          status: filters.status || 'paid'
        }

        if (filters.employeeId) params.employeeId = filters.employeeId
        if (filters.orderType) params.orderType = filters.orderType
        if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod
        if (filters.dateRange?.length === 2) {
          params.dateStart = filters.dateRange[0].format('YYYY-MM-DD')
          params.dateEnd = filters.dateRange[1].format('YYYY-MM-DD')
        }
        if (filters.orderId) params.orderId = filters.orderId.trim()

        const response = await api.get(
          `/api/businesses/${user.businessId}/checkout/employee-orders`,
          { params }
        )

        const responseData = response.data || {}
        const recordsData =
          responseData.data?.records ||
          responseData.records ||
          responseData.data ||
          []
        const total =
          responseData.total ||
          responseData.pagination?.total ||
          recordsData.length

        setRecords(Array.isArray(recordsData) ? recordsData : [])
        pageSizeRef.current = pageSize // 更新 ref
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: total || 0
        }))
      } catch (error) {
        console.error('獲取員工訂單記錄失敗:', error)
        message.error(
          error.response?.data?.message ||
            error.message ||
            '無法獲取記錄，請稍後再試'
        )
      } finally {
        setLoading(false)
        loadingRef.current = false
      }
    },
    [user?.businessId] // 移除 isBrandPlan 依賴，在函數內部檢查
  )

  const handleSearch = useCallback(async () => {
    if (loadingRef.current) return // 防止重複調用
    const values = form.getFieldsValue()
    const nextFilters = {
      employeeId: values.employeeId || undefined,
      orderType: values.orderType || undefined,
      paymentMethod: values.paymentMethod || undefined,
      dateRange: values.dateRange || undefined,
      orderId: values.orderId || undefined,
      status: values.status || 'paid'
    }
    filtersRef.current = nextFilters
    setPagination((prev) => ({ ...prev, current: 1 }))
    // 使用 ref 中的 pageSize，避免依賴狀態
    await fetchRecords(1, pageSizeRef.current, nextFilters)
  }, [fetchRecords, form])

  const handleReset = useCallback(async () => {
    if (loadingRef.current) return // 防止重複調用
    form.resetFields()
    // 重置後狀態回到預設「有效」，與初始載入一致
    form.setFieldsValue({ status: 'paid' })
    filtersRef.current = { status: 'paid' }
    setPagination((prev) => ({ ...prev, current: 1 }))
    // 使用 ref 中的 pageSize，避免依賴狀態
    await fetchRecords(1, pageSizeRef.current, { status: 'paid' })
  }, [fetchRecords, form])

  const handlePageChange = useCallback(
    (page, pageSize) => {
      fetchRecords(page, pageSize, filtersRef.current)
    },
    [fetchRecords]
  )

  // === 作廢員工訂單 ===
  // 後端 voidEmployeeOrder 會：
  //   1) 將 EmployeeOrder.status 設為 'voided'
  //   2) 連動取消對應的 InventoryUsage 並回補庫存
  //   3) 若原本是薪資扣款，自動回沖當月薪資扣款金額
  // 兩邊（庫存使用紀錄、員工自購記錄）因此會保持同步。
  const voidOrder = useCallback(
    async (record, reason) => {
      try {
        setLoading(true)
        await api.post(
          `/api/businesses/${user.businessId}/checkout/employee-orders/${record._id || record.orderId}/void`,
          {
            reason: reason || '從員工自購紀錄刪除',
            note: '由員工自購記錄頁面手動刪除'
          }
        )
        message.success('已作廢此員工訂單，對應的庫存使用紀錄也已連動取消')
        // 重新載入（保留現有篩選與分頁）
        await fetchRecords(
          pagination.current,
          pagination.pageSize,
          filtersRef.current
        )
      } catch (error) {
        console.error('作廢員工訂單失敗:', error)
        message.error(
          error.response?.data?.message ||
            error.message ||
            '作廢失敗，請稍後再試'
        )
      } finally {
        setLoading(false)
      }
    },
    [user?.businessId, fetchRecords, pagination.current, pagination.pageSize]
  )

  const handleVoidOrder = useCallback(
    (record) => {
      if (record.status === 'voided') {
        message.info('此訂單已是作廢狀態')
        return
      }

      const employee =
        employees.find((e) => e._id === record.employeeId)?.name ||
        record.employeeName ||
        '員工'
      const orderTypeLabel =
        orderTypeLabels[record.orderType || record.type]?.label ||
        record.orderType ||
        ''
      const amount =
        record.totalAmount || record.totals?.grandTotal || 0

      Modal.confirm({
        title: '確定要刪除此員工訂單嗎？',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>
              員工：<b>{employee}</b>
            </p>
            <p>
              類型：<b>{orderTypeLabel}</b>　金額：
              <b>${Number(amount).toLocaleString()}</b>
            </p>
            <p style={{ color: '#ff4d4f', marginTop: 8 }}>
              刪除後將同步：
              <br />・把訂單標記為「已作廢」（保留稽核紀錄）
              <br />・取消對應的庫存使用紀錄並回補庫存
              <br />・若為薪資扣款，回沖當月扣款金額
            </p>
          </div>
        ),
        okText: '確定刪除',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => voidOrder(record, '員工訂單記錄頁面手動刪除')
      })
    },
    [employees, voidOrder]
  )

  useEffect(() => {
    if (!hasFeature('checkout')) return
    fetchEmployees()
  }, [fetchEmployees, hasFeature]) // 移除 isBrandPlan 依賴

  useEffect(() => {
    if (!hasFeature('checkout') || initializedRef.current) return
    // 只在組件掛載時執行一次
    initializedRef.current = true
    fetchRecords(1, pageSizeRef.current, {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 只在掛載時執行一次

  if (!permissionsLoaded) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageContainer>
    )
  }

  if (!hasFeature('checkout')) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <p style={{ fontSize: '16px', color: colors.text.secondary }}>
              此功能需要更高級別的會員方案才能使用
            </p>
            <Button
              type='primary'
              onClick={() => navigate('/settings/membership')}
              style={{ marginTop: '16px' }}
            >
              查看會員方案
            </Button>
          </div>
        </Card>
      </PageContainer>
    )
  }

  const columns = [
    {
      title: '訂單編號',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 150,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
          {text || '--'}
        </span>
      )
    },
    {
      title: '員工',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 120,
      render: (text, record) => {
        const employee = employees.find((e) => e._id === record.employeeId)
        return employee?.name || text || '--'
      }
    },
    {
      title: '類型',
      dataIndex: 'orderType',
      key: 'orderType',
      width: 100,
      render: (type) => {
        const typeInfo = orderTypeLabels[type] || {
          label: type,
          color: 'default'
        }
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>
      }
    },
    {
      title: '購買方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 140,
      render: (method) => paymentMethodLabels[method] || method || '--'
    },
    {
      title: '商品項目',
      key: 'products',
      width: 200,
      render: (_, record) => {
        const products = record.products || []
        if (products.length === 0)
          return <span style={{ color: colors.text.tertiary }}>無</span>
        return (
          <div>
            {products.slice(0, 2).map((product, index) => (
              <div key={index} style={{ fontSize: '13px' }}>
                {product.name || product.productName} × {product.quantity} -{' '}
                {formatCurrency(product.price)}
              </div>
            ))}
            {products.length > 2 && (
              <span style={{ color: colors.text.tertiary, fontSize: '12px' }}>
                等 {products.length} 項
              </span>
            )}
          </div>
        )
      }
    },
    {
      title: '總金額',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      render: (amount, record) => {
        const total = amount || record.totals?.grandTotal || 0
        return (
          <span style={{ fontWeight: 500, color: colors.text.primary }}>
            {formatCurrency(total)}
          </span>
        )
      }
    },
    {
      title: '備註',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      render: (text) => (
        <div
          style={{
            fontSize: '12px',
            color: colors.text.secondary,
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={text}
        >
          {text || '--'}
        </div>
      )
    },
    {
      title: '日期時間',
      key: 'datetime',
      width: 160,
      render: (_, record) => {
        const date = record.date || record.createdAt
        const time = record.time
        if (!date) return '--'
        const dateStr = dayjs(date).format('YYYY-MM-DD')
        return (
          <div style={{ fontSize: '13px' }}>
            <div>{dateStr}</div>
            {time && <div style={{ color: colors.text.tertiary }}>{time}</div>}
          </div>
        )
      }
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          paid: { label: '已付款', color: 'success' },
          paid_partially: { label: '部分付款', color: 'warning' },
          confirmed: { label: '已確認', color: 'blue' },
          voided: { label: '已作廢', color: 'error' }
        }
        const statusInfo = statusMap[status] || {
          label: status,
          color: 'default'
        }
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type='link'
          size='small'
          danger
          icon={<DeleteOutlined />}
          disabled={record.status === 'voided'}
          onClick={(e) => {
            e.stopPropagation()
            handleVoidOrder(record)
          }}
        >
          {record.status === 'voided' ? '已作廢' : '刪除'}
        </Button>
      )
    }
  ]

  // 手機版：表格轉卡片
  const renderMobileCards = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      )
    }
    if (!records || records.length === 0) {
      return (
        <Empty
          description='目前沒有符合條件的記錄'
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: 24 }}
        />
      )
    }
    return (
      <>
        <MobileRecordList>
          {records.map((record) => {
            const employee = employees.find((e) => e._id === record.employeeId)
            const employeeName =
              employee?.name || record.employeeName || '--'
            const typeInfo = orderTypeLabels[record.orderType] || {
              label: record.orderType,
              color: 'default'
            }
            const statusInfo = statusLabels[record.status] || {
              label: record.status,
              color: 'default'
            }
            const total = record.totalAmount || record.totals?.grandTotal || 0
            const products = record.products || []
            const date = record.date || record.createdAt
            const dateStr = date ? dayjs(date).format('YYYY-MM-DD') : '--'
            return (
              <MobileRecordCard
                key={record.orderId || record._id}
                dimmed={record.status === 'voided'}
                title={employeeName}
                subtitle={
                  <span style={{ fontFamily: 'monospace' }}>
                    {record.orderId || '--'}
                  </span>
                }
                extra={
                  <>
                    <Tag color={typeInfo.color} style={{ marginRight: 0 }}>
                      {typeInfo.label}
                    </Tag>
                    <Tag color={statusInfo.color} style={{ marginRight: 0 }}>
                      {statusInfo.label}
                    </Tag>
                  </>
                }
                rows={[
                  {
                    label: '日期時間',
                    value: `${dateStr}${record.time ? `　${record.time}` : ''}`
                  },
                  {
                    label: '購買方式',
                    value:
                      paymentMethodLabels[record.paymentMethod] ||
                      record.paymentMethod ||
                      '--'
                  },
                  {
                    label: '商品項目',
                    value:
                      products.length === 0
                        ? '無'
                        : products
                            .slice(0, 3)
                            .map((p) => `${p.name || p.productName} ×${p.quantity}`)
                            .join('、') +
                          (products.length > 3
                            ? ` 等 ${products.length} 項`
                            : '')
                  },
                  {
                    label: '總金額',
                    value: formatCurrency(total),
                    valueStyle: { fontWeight: 600 }
                  },
                  {
                    label: '備註',
                    value: record.remarks,
                    hidden: !record.remarks
                  }
                ]}
                actions={
                  <Button
                    size='small'
                    danger
                    icon={<DeleteOutlined />}
                    disabled={record.status === 'voided'}
                    onClick={() => handleVoidOrder(record)}
                  >
                    {record.status === 'voided' ? '已作廢' : '刪除'}
                  </Button>
                }
              />
            )
          })}
        </MobileRecordList>
        <MobileCardPagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={handlePageChange}
        />
      </>
    )
  }

  return (
    <PageContainer>
      <PageTitle>員工自購或領料記錄</PageTitle>

      <FilterCard>
        <Form form={form} layout='vertical'>
          <FilterRow>
            <Form.Item name='employeeId' label='員工'>
              <Select
                placeholder='選擇員工'
                allowClear
                showSearch
                optionFilterProp='children'
              >
                {employees.map((employee) => (
                  <Option key={employee._id} value={employee._id}>
                    {employee.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='orderType' label='類型'>
              <Select placeholder='選擇類型' allowClear>
                <Option value='purchase'>自購</Option>
                <Option value='material'>領料</Option>
                <Option value='gift'>贈出</Option>
              </Select>
            </Form.Item>

            <Form.Item name='paymentMethod' label='購買方式'>
              <Select placeholder='選擇購買方式' allowClear>
                <Option value='cash'>現金</Option>
                <Option value='nonCash'>非現金</Option>
                <Option value='salaryDeduct'>由薪資扣除</Option>
              </Select>
            </Form.Item>

            <Form.Item name='dateRange' label='日期區間'>
              <RangePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD'
                placeholder={['開始日期', '結束日期']}
              />
            </Form.Item>

            <Form.Item name='orderId' label='訂單編號'>
              <Input
                placeholder='輸入訂單編號'
                prefix={<SearchOutlined />}
                allowClear
              />
            </Form.Item>

            <Form.Item
              name='status'
              label='狀態'
              tooltip='預設僅顯示有效訂單；對帳時可切換查看已作廢訂單'
              initialValue='paid'
            >
              <Select placeholder='選擇狀態'>
                <Option value='paid'>有效（已付款）</Option>
                <Option value='voided'>已作廢</Option>
                <Option value='all'>全部</Option>
              </Select>
            </Form.Item>
          </FilterRow>

          <FilterActions>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              搜尋
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
            <Button onClick={() => navigate('/checkout/employee-order')}>
              新增訂單
            </Button>
          </FilterActions>
        </Form>
      </FilterCard>

      <TableCard>
        {isMobile ? (
          renderMobileCards()
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={records}
              rowKey={(record) =>
                record.orderId || record._id || Math.random()
              }
              loading={loading}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <Empty
                    description='目前沒有符合條件的記錄'
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )
              }}
            />

            {records.length > 0 && (
              <PaginationWrapper>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger
                  showTotal={(total) => `共 ${total} 筆記錄`}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </TableCard>
    </PageContainer>
  )
}

export default EmployeeOrderRecordsPage
